import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import type { IntegrationOption } from '../types';

const IntegrationShowcase = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const integrations: IntegrationOption[] = [
    { id: '1', name: 'Gmail', icon: 'Mail', category: 'Communication', description: 'Email automation and management', popular: true },
    { id: '2', name: 'Slack', icon: 'MessageSquare', category: 'Communication', description: 'Team messaging and notifications', popular: true },
    { id: '3', name: 'Google Sheets', icon: 'FileSpreadsheet', category: 'Productivity', description: 'Spreadsheet data management', popular: true },
    { id: '4', name: 'Airtable', icon: 'Database', category: 'Productivity', description: 'Database and project management', popular: true },
    { id: '5', name: 'HubSpot', icon: 'Users', category: 'CRM', description: 'Customer relationship management', popular: true },
    { id: '6', name: 'Salesforce', icon: 'Building2', category: 'CRM', description: 'Enterprise CRM platform', popular: false },
    { id: '7', name: 'Shopify', icon: 'ShoppingCart', category: 'E-commerce', description: 'Online store management', popular: true },
    { id: '8', name: 'WooCommerce', icon: 'Store', category: 'E-commerce', description: 'WordPress e-commerce', popular: false },
    { id: '9', name: 'Stripe', icon: 'CreditCard', category: 'Payment', description: 'Payment processing', popular: true },
    { id: '10', name: 'PayPal', icon: 'Wallet', category: 'Payment', description: 'Online payments', popular: false },
    { id: '11', name: 'Twilio', icon: 'Phone', category: 'Communication', description: 'SMS and voice services', popular: false },
    { id: '12', name: 'Mailchimp', icon: 'Mail', category: 'Marketing', description: 'Email marketing campaigns', popular: true },
    { id: '13', name: 'Facebook Ads', icon: 'Facebook', category: 'Marketing', description: 'Social media advertising', popular: false },
    { id: '14', name: 'Google Analytics', icon: 'BarChart3', category: 'Analytics', description: 'Web analytics and reporting', popular: true },
    { id: '15', name: 'Notion', icon: 'FileText', category: 'Productivity', description: 'Workspace and documentation', popular: true },
    { id: '16', name: 'Trello', icon: 'Kanban', category: 'Productivity', description: 'Project management boards', popular: false },
    { id: '17', name: 'Asana', icon: 'CheckSquare', category: 'Productivity', description: 'Task and project tracking', popular: false },
    { id: '18', name: 'Dropbox', icon: 'Cloud', category: 'Storage', description: 'Cloud file storage', popular: false },
    { id: '19', name: 'Google Drive', icon: 'HardDrive', category: 'Storage', description: 'Cloud storage and collaboration', popular: true },
    { id: '20', name: 'Zoom', icon: 'Video', category: 'Communication', description: 'Video conferencing', popular: true }
  ];

  const categories = ['all', ...Array.from(new Set(integrations.map(i => i.category)))];

  const filteredIntegrations = selectedCategory === 'all' 
    ? integrations 
    : integrations.filter(i => i.category === selectedCategory);

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Plug" size={16} />
            <span>1000+ Integrations</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-text-primary mb-4">
            Connect Your Entire Tech Stack
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Seamlessly integrate with the tools you already use. Build powerful workflows that connect your favorite apps and services.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-12 justify-center">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-300 ${
                selectedCategory === category
                  ? 'bg-primary text-white' :'bg-card text-text-secondary hover:bg-surface'
              }`}
            >
              {category === 'all' ? 'All Categories' : category}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredIntegrations.map((integration) => (
            <div
              key={integration.id}
              className="relative bg-card rounded-xl p-6 border border-border hover:border-primary hover:shadow-lg transition-all duration-300 group"
            >
              {integration.popular && (
                <div className="absolute -top-2 -right-2 bg-accent text-white text-xs font-bold px-2 py-1 rounded-full">
                  Popular
                </div>
              )}
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                  <Icon name={integration.icon} size={32} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">{integration.name}</h3>
                  <p className="text-xs text-text-secondary mt-1">{integration.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-text-secondary mb-6">
            Don't see your tool? We can integrate with any app that has an API.
          </p>
          <button className="inline-flex items-center space-x-2 bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300">
            <Icon name="Plus" size={20} />
            <span>Request Custom Integration</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default IntegrationShowcase;